import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getApiUrl, getDemoCredentials, isDemoMode } from '../../config/demoMode';
import OturumDisiSayfa from '../../components/OturumDisiSayfa';
import { IkonGoz, IkonGozKapali } from '../../components/Ikonlar';

const INP = 'w-full bg-white border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-sm font-semibold text-gray-800 ' +
            'placeholder-gray-300 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100 transition-all';
const LBL = 'block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2.5 ml-1';

export default function Login() {
  const [kullaniciAdi, setKullaniciAdi] = useState('');
  const [sifre, setSifre] = useState('');
  const [sifreGoster, setSifreGoster] = useState(false);
  const [hata, setHata] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);

  // Otel seçim aşaması
  const [otelSecimAsamasi, setOtelSecimAsamasi] = useState(false);
  const [otelSecimData, setOtelSecimData] = useState(null);

  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const demoMode = isDemoMode();
  const demoCredentials = getDemoCredentials();
  const demoQuickLoginEnabled = demoMode && demoCredentials.username && demoCredentials.password;
  const apiUrl = getApiUrl();

  const handleLogin = async (e) => {
    e.preventDefault();
    setHata('');
    setYukleniyor(true);

    try {
      if (!apiUrl) {
        setHata('Demo API adresi yapılandırılmamış.');
        return;
      }
      const data = await login(kullaniciAdi, sifre);

      if (data?.demoExpired) {
        navigate('/demo-suresi-doldu');
      } else if (data?.otelSecimGerekli) {
        setOtelSecimData(data);
        setOtelSecimAsamasi(true);
      } else if (data?.personelId) {
        loginUser(data);
        navigate(data.firmaId ? '/kamp-amiri' : '/');
      } else {
        setHata('Kullanıcı adı veya şifre hatalı.');
      }
    } catch (err) {
      setHata('Sunucuya bağlanılamadı.');
    } finally {
      setYukleniyor(false);
    }
  };

  const handleOtelSec = async (otelId) => {
    setYukleniyor(true);
    setHata('');
    try {
      if (!apiUrl) {
        setHata('Demo API adresi yapılandırılmamış.');
        return;
      }
      const res = await fetch(`${apiUrl}/auth/otel-sec`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ personelId: otelSecimData.personelId, otelId })
      });
      const data = await res.json();
      if (data?.demoExpired) {
        navigate('/demo-suresi-doldu');
        return;
      }
      if (!res.ok) {
        setHata(data?.message || 'Otel seçimi başarısız.');
        return;
      }
      loginUser(data);
      navigate(data.firmaId ? '/kamp-amiri' : '/');
    } catch {
      setHata('Sunucuya bağlanılamadı.');
    } finally {
      setYukleniyor(false);
    }
  };

  const hataBanner = hata && (
    <div className="bg-rose-50 border-2 border-rose-100 text-rose-600 text-sm font-semibold rounded-2xl px-5 py-4 mb-5">
      {hata}
    </div>
  );

  // ── Otel seçimi ──────────────────────────────────────────
  if (otelSecimAsamasi && otelSecimData) {
    return (
      <OturumDisiSayfa altYazi="Birden fazla tesisi olan hesaplarda hangi otelle çalışacağınızı seçersiniz. İstediğiniz zaman çıkış yapıp diğerine geçebilirsiniz.">
        <h1 className="text-3xl font-black text-gray-800 tracking-tight">Otel Seçin</h1>
        <p className="text-gray-400 text-sm font-semibold mt-2 mb-8">
          Merhaba {otelSecimData.adSoyad}, hangi otelle devam etmek istiyorsunuz?
        </p>

        {hataBanner}

        <div className="space-y-3">
          {otelSecimData.oteller.map(otel => (
            <button
              key={otel.otelId}
              onClick={() => handleOtelSec(otel.otelId)}
              disabled={yukleniyor}
              className="w-full bg-white border-2 border-gray-100 hover:border-violet-300 hover:bg-violet-50/50
                         disabled:opacity-50 rounded-2xl py-4 px-5 text-left flex items-center gap-4
                         transition-all active:scale-[0.99] group"
            >
              <div className="w-11 h-11 rounded-xl bg-violet-50 group-hover:bg-violet-100 flex items-center justify-center flex-shrink-0 text-lg transition-colors font-emoji">
                🏨
              </div>
              <span className="font-bold text-gray-700">{otel.ad}</span>
              <span className="ml-auto text-gray-300 group-hover:text-violet-400 transition-colors">→</span>
            </button>
          ))}
        </div>

        <button
          onClick={() => { setOtelSecimAsamasi(false); setOtelSecimData(null); }}
          className="w-full mt-6 text-gray-400 hover:text-gray-600 text-[10px] font-black uppercase tracking-widest py-3 transition-colors"
        >
          ← Geri dön
        </button>
      </OturumDisiSayfa>
    );
  }

  // ── Giriş ────────────────────────────────────────────────
  return (
    <OturumDisiSayfa altYazi="Resepsiyondan muhasebeye, kat hizmetlerinden raporlamaya kadar günlük işleyişin tamamı tek panelde.">
      <h1 className="text-3xl font-black text-gray-800 tracking-tight">Hoş geldiniz</h1>
      <p className="text-gray-400 text-sm font-semibold mt-2 mb-8">
        {demoMode ? 'Demo panele giriş yapın' : 'Devam etmek için giriş yapın'}
      </p>

      {demoMode && (
        <div className="bg-sky-50 border-2 border-sky-100 text-sky-700 text-sm font-semibold rounded-2xl px-5 py-4 mb-5">
          Bu ortam demo verileriyle çalışır. Canlı otel verilerine bağlı değildir.
        </div>
      )}

      {hataBanner}

      <form onSubmit={handleLogin} className="space-y-5">
        <div>
          <label className={LBL}>Kullanıcı Adı</label>
          <input
            type="text"
            value={kullaniciAdi}
            onChange={(e) => setKullaniciAdi(e.target.value)}
            placeholder="kullanici_adi"
            autoComplete="username"
            className={INP}
          />
        </div>

        <div>
          <label className={LBL}>Şifre</label>
          <div className="relative">
            <input
              type={sifreGoster ? 'text' : 'password'}
              value={sifre}
              onChange={(e) => setSifre(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              className={INP + ' pr-14'}
            />
            <button
              type="button"
              onClick={() => setSifreGoster(g => !g)}
              aria-label={sifreGoster ? 'Şifreyi gizle' : 'Şifreyi göster'}
              title={sifreGoster ? 'Şifreyi gizle' : 'Şifreyi göster'}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-violet-500 transition-colors"
            >
              {sifreGoster ? <IkonGozKapali /> : <IkonGoz />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={yukleniyor}
          className="w-full bg-gradient-to-r from-violet-600 to-indigo-700 hover:-translate-y-0.5 active:translate-y-0
                     disabled:opacity-50 disabled:hover:translate-y-0 text-white rounded-2xl py-4
                     text-[11px] font-black uppercase tracking-[0.2em] shadow-xl shadow-violet-200 transition-all"
        >
          {yukleniyor ? 'Giriş yapılıyor…' : 'Giriş Yap'}
        </button>

        {demoQuickLoginEnabled && (
          <button
            type="button"
            onClick={() => {
              setKullaniciAdi(demoCredentials.username);
              setSifre(demoCredentials.password);
            }}
            className="w-full bg-white border-2 border-gray-100 hover:bg-gray-50 text-gray-500 rounded-2xl py-3.5
                       text-[10px] font-black uppercase tracking-widest transition-all"
          >
            Demo Bilgilerini Doldur
          </button>
        )}
      </form>
    </OturumDisiSayfa>
  );
}
